<template>
  <div class="app-layout">
    <!-- Only show drawer if user is logged in -->
    <template v-if="user">
      <aside :class="['app-drawer', { open: drawerOpen }]">
        <div class="drawer-header">
          <img class="logo" src="../assets/postmate-logo.png" alt="Logo" />
          <span class="brand">PostMate</span>
          <button class="drawer-toggle d-md-none" @click="toggleDrawer">
            <b-icon :icon="drawerOpen ? 'x' : 'list'" font-scale="1.5" />
          </button>
        </div>
        <nav class="drawer-nav">
          <b-nav vertical>
            <b-nav-item to="/dashboard" exact>
              <b-icon icon="speedometer2" class="mr-5" /> Dashboard
            </b-nav-item>
            <div class="drawer-collapse-group">
              <div
                class="drawer-collapse-header"
                @click="toggleCollapse('tokens')"
                :aria-expanded="collapse.tokens"
              >
                <b-icon icon="key" class="mr-2" /> Tokens
                <b-icon
                  :icon="collapse.tokens ? 'chevron-up' : 'chevron-down'"
                  class="ml-auto"
                />
              </div>
              <b-collapse :visible="collapse.tokens">
                <b-nav-item to="/new-token" class="drawer-collapse-item">
                  <b-icon icon="plus-circle" class="mr-2" /> New
                </b-nav-item>
                <b-nav-item to="/tokens" class="drawer-collapse-item">
                  <b-icon icon="list" class="mr-2" /> List
                </b-nav-item>
              </b-collapse>
            </div>
            <div class="drawer-collapse-group">
              <div
                class="drawer-collapse-header"
                @click="toggleCollapse('jobs')"
                :aria-expanded="collapse.jobs"
              >
                <b-icon icon="briefcase" class="mr-2" /> Jobs
                <b-icon
                  :icon="collapse.jobs ? 'chevron-up' : 'chevron-down'"
                  class="ml-auto"
                />
              </div>
              <b-collapse :visible="collapse.jobs">
                <b-nav-item to="/new-automation" class="drawer-collapse-item">
                  <b-icon icon="plus-circle" class="mr-2" /> New
                </b-nav-item>
                <b-nav-item to="/jobs/all" class="drawer-collapse-item">
                  <b-icon icon="list" class="mr-2" /> List
                </b-nav-item>
              </b-collapse>
            </div>
            <div class="drawer-collapse-group">
              <div
                class="drawer-collapse-header"
                @click="toggleCollapse('bulkDelete')"
                :aria-expanded="collapse.bulkDelete"
              >
                <b-icon icon="trash" class="mr-2" /> Bulk Delete
                <b-icon
                  :icon="collapse.bulkDelete ? 'chevron-up' : 'chevron-down'"
                  class="ml-auto"
                />
              </div>
              <b-collapse :visible="collapse.bulkDelete">
                <b-nav-item to="/bulk-delete/new" class="drawer-collapse-item">
                  <b-icon icon="plus-circle" class="mr-2" /> New
                </b-nav-item>
                <b-nav-item to="/bulk-delete/list" class="drawer-collapse-item">
                  <b-icon icon="list" class="mr-2" /> List
                </b-nav-item>
              </b-collapse>
            </div>
            <template v-if="user && user.role === 0">
              <div class="drawer-collapse-group">
                <div
                  class="drawer-collapse-header"
                  @click="toggleCollapse('users')"
                  :aria-expanded="collapse.users"
                >
                  <b-icon icon="people" class="mr-2" /> Users
                  <b-icon
                    :icon="collapse.users ? 'chevron-up' : 'chevron-down'"
                    class="ml-auto"
                  />
                </div>
                <b-collapse :visible="collapse.users">
                  <b-nav-item to="/users/new" class="drawer-collapse-item">
                    <b-icon icon="person-plus" class="mr-2" /> New
                  </b-nav-item>
                  <b-nav-item to="/users/all" class="drawer-collapse-item">
                    <b-icon icon="people" class="mr-2" /> List
                  </b-nav-item>
                </b-collapse>
              </div>
            </template>
          </b-nav>
        </nav>
        <div class="drawer-divider"></div>
        <div class="drawer-footer mt-auto">
          <div v-if="user" class="user-info">
            <b-icon icon="person-circle" class="mr-2" />
            <span class="user-name">{{ user.name }}</span>
          </div>
          <b-button
            v-if="user"
            @click="handleLogout"
            variant="outline-danger"
            class="logout-btn mt-2 w-100"
          >
            <b-icon icon="box-arrow-right" class="mr-2" /> Log Out
          </b-button>
          <div class="watermark mt-3">
            Managed by
            <a href="https://shujja.com" target="_blank" rel="noopener"
              >shujja.com</a
            >
          </div>
        </div>
      </aside>
      <div
        v-if="drawerOpen"
        class="drawer-overlay d-md-none"
        @click="toggleDrawer"
      ></div>
    </template>

    <main :class="['main-content', { 'ml-0': !user }]">
      <div class="main-content-inner">
        <b-container fluid class="pt-3 px-3 px-md-5">
          <router-view />
        </b-container>
      </div>
    </main>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { BIcon } from "bootstrap-vue";

export default {
  components: { BIcon },
  data() {
    return {
      drawerOpen: false,
      collapse: {
        tokens: false,
        jobs: false,
        bulkDelete: false,
        users: false,
      },
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
  },
  methods: {
    ...mapActions(["logout"]),
    handleLogout() {
      this.logout();
      this.$router.push("/login");
    },
    toggleDrawer() {
      this.drawerOpen = !this.drawerOpen;
    },
    toggleCollapse(section) {
      // Close all sections first
      Object.keys(this.collapse).forEach((key) => {
        this.collapse[key] = false;
      });
      // Then open the clicked section
      this.collapse[section] = true;
    },
  },
  watch: {
    $route() {
      this.drawerOpen = false;
    },
  },
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background: #f6f7fb;
}
.app-drawer {
  width: 250px;
  background: #fff;
  color: #22223b;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1050;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.08);
  border-right: 1px solid #ececec;
  border-radius: 0 24px 24px 0;
}
.app-drawer .drawer-header {
  display: flex;
  align-items: center;
  padding: 28px 22px 18px 22px;
  border-bottom: 1px solid #f1f3f6;
  position: relative;
  min-height: 80px;
}
.app-drawer .logo {
  width: 40px;
  margin-right: 12px;
  filter: drop-shadow(0 2px 8px #0002);
}
.app-drawer .brand {
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  color: #22223b;
}
.drawer-toggle {
  background: none;
  border: none;
  color: #22223b;
  margin-left: auto;
  outline: none;
  padding: 0 2px;
  transition: color 0.2s;
}
.drawer-toggle:hover {
  color: #0077ff;
}
.drawer-nav {
  flex: 1 1 auto;
  padding: 24px 0 0 0;
}
.drawer-nav .nav-link,
.drawer-nav .dropdown-item {
  color: #22223b !important;
  font-size: 1.08rem;
  font-weight: 500;
  border-radius: 10px;
  margin-bottom: 4px;
  padding: 10px 22px 10px 36px;
  position: relative;
  transition: background 0.18s, color 0.18s, box-shadow 0.18s;
  overflow: hidden;
}
.drawer-nav .nav-link.active,
.drawer-nav .nav-link.router-link-exact-active,
.drawer-nav .nav-link:hover,
.drawer-nav .dropdown-item:hover {
  background: #f1f5f9 !important;
  color: #0077ff !important;
  box-shadow: 0 2px 12px 0 rgba(96, 165, 250, 0.08);
}
.drawer-nav .b-nav-item-dropdown {
  width: 100%;
}
.drawer-divider {
  height: 1px;
  background: #ececec;
  margin: 0 18px 0 18px;
  border-radius: 2px;
}
.drawer-footer {
  padding: 24px 22px 28px 22px;
  border-top: 1px solid #f1f3f6;
  background: #fff;
  border-bottom-right-radius: 24px;
}
.user-info {
  display: flex;
  align-items: center;
  font-size: 1.08rem;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
  letter-spacing: 0.2px;
}
.user-name {
  font-weight: 700;
  color: #22223b;
  margin-left: 2px;
}
.logout-btn {
  font-weight: 700;
  border-radius: 8px;
  font-size: 1.05rem;
  padding: 10px 0;
  box-shadow: 0 2px 8px 0 rgba(220, 38, 38, 0.08);
  transition: background 0.18s, color 0.18s;
}
.logout-btn:hover {
  background: #ef4444 !important;
  color: #fff !important;
  border-color: #ef4444 !important;
}
.main-content {
  flex: 1 1 auto;
  margin-left: 250px;
  min-height: 100vh;
  background: #004797;
  transition: margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
}
.main-content-inner {
  margin: 32px 24px 32px 24px;
  border-radius: 24px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.08);
  min-height: 90vh;
  transition: background 0.2s, box-shadow 0.2s;
  overflow: hidden;
}
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1049;
  transition: background 0.2s;
}
@media (max-width: 991.98px) {
  .app-drawer {
    transform: translateX(-100%);
    position: fixed;
    z-index: 1050;
  }
  .app-drawer.open {
    transform: translateX(0);
  }
  .main-content {
    margin-left: 0;
  }
}
@media (max-width: 767.98px) {
  .main-content-inner {
    margin: 16px 4px 16px 4px;
    border-radius: 14px;
  }
  .app-drawer {
    border-radius: 0 18px 18px 0;
  }
  .drawer-footer {
    border-bottom-right-radius: 18px;
  }
}
@media (max-width: 575.98px) {
  .app-drawer {
    width: 90vw;
    min-width: 0;
    max-width: 320px;
  }
}
.drawer-collapse-group {
  margin-bottom: 8px;
}
.drawer-collapse-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 10px 22px 10px 36px;
  color: #22223b;
  font-size: 1.09rem;
  font-weight: 500;
  border-radius: 10px;
  transition: background 0.18s, color 0.18s;
  user-select: none;
}
.drawer-collapse-header:hover,
.drawer-collapse-header[aria-expanded="true"] {
  background: #f1f5f9;
  color: #0077ff;
}
.drawer-collapse-item {
  padding-left: 54px !important;
  font-size: 1.01rem;
  color: #64748b !important;
  border-radius: 8px;
  margin-bottom: 2px;
  transition: background 0.16s, color 0.16s;
}
.drawer-collapse-item.router-link-exact-active,
.drawer-collapse-item.active,
.drawer-collapse-item:hover {
  color: #fff !important;
}
.drawer-collapse-item .b-icon {
  font-size: 1.05em;
  margin-right: 10px;
}
.drawer-collapse-header .ml-auto {
  margin-left: auto !important;
}
.drawer-collapse-header .b-icon {
  font-size: 1.1em;
  margin-right: 10px;
}
.ml-0 {
  margin-left: 0 !important;
}
.watermark {
  text-align: center;
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.watermark a {
  color: #0077ff;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.watermark a:hover {
  color: #0066dd;
}
</style>
